import { type GridWithFilters } from '@strata/react-grid';
import { type ComponentProps, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from 'store';
import {
    type Grid,
    type GridState,
    setCurrentPage,
    setFilterPaneWidth,
    setGridActiveFilters,
    setGridSearch,
    setGridSort,
} from 'store/grids/gridsSlice';

/**
 * Reduces a filter state to the selections that actually narrow the query, so that equivalent
 * states compare equal. The filter pane reports "no filters" as an object of empty selections on
 * mount, which a plain compare reads as a change from undefined and re-triggers every query keyed
 * on activeFilters.
 */
const filterComparisonKey = (filters: GridState['activeFilters']) =>
    JSON.stringify(
        Object.entries(filters ?? {})
            .filter(([, values]) => (values?.length ?? 0) > 0)
            .sort(([keyA], [keyB]) => keyA.localeCompare(keyB)),
    );

export const useGridState = (
    grid: Grid,
    initialSortProperties?: GridState['sort'],
): GridState & {
    updateGridSort: ComponentProps<typeof GridWithFilters>['onSort'];
    updateGridSearch: ComponentProps<typeof GridWithFilters>['onSearch'];
    updateGridActiveFilters: ComponentProps<typeof GridWithFilters>['onFilter'];
    updateFilterPaneWidth: ComponentProps<typeof GridWithFilters>['onFilterPaneResize'];
    updateCurrentPage: ComponentProps<typeof GridWithFilters>['onPageChange'];
} => {
    const dispatch = useAppDispatch();
    const { sort, search, activeFilters, filterPaneWidth, page } = useAppSelector(
        (state) => state.grids[grid],
    );
    useEffect(() => {
        if (!sort) {
            dispatch(setGridSort({ grid, sort: initialSortProperties }));
        }
    }, [grid, sort, initialSortProperties, dispatch]);
    return {
        // The effect above only lands the initial sort on the second render, so fall back to it
        // here. Otherwise the first render queries unsorted and the sort re-triggers the request.
        sort: sort ?? initialSortProperties,
        // A cleared search box reports '', which is the same query as never having searched.
        // Reporting one value for both keeps them on a single cache entry.
        search: search === '' ? undefined : search,
        activeFilters,
        filterPaneWidth,
        page,
        updateGridSort: (sortProperties) => dispatch(setGridSort({ grid, sort: sortProperties })),
        updateGridSearch: (searchText) => dispatch(setGridSearch({ grid, search: searchText })),
        updateGridActiveFilters: (filters) => {
            // Need to deep compare filter objects to avoid infinite rerender loop when setting initial filters
            // May want to look into limiting re-renders in internal component logic
            if (filterComparisonKey(activeFilters) !== filterComparisonKey(filters)) {
                dispatch(setGridActiveFilters({ grid, activeFilters: filters }));
            }
        },
        updateFilterPaneWidth: (width) =>
            dispatch(setFilterPaneWidth({ grid, filterPaneWidth: width })),
        updateCurrentPage: (currentPage) => dispatch(setCurrentPage({ grid, page: currentPage })),
    };
};
